import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    ApiClientService,
    AuthenticationService,
    CachedValuesService,
    PermissionService,
    ToastService,
    RightPanelService
} from '@services/index';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { Align, Permission } from 'app/enums';
import { SortType } from 'app/types';
import { Filter, FilterForm } from './user-list.model';
import { BehaviorSubject, EMPTY, filter, finalize, map, Observable, of, switchMap, take } from 'rxjs';
import { GetUsersResponse, UserResponse } from '@services/api-client/responses';
import { sortStrings } from '@helpers/sort-string-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ModalHelper } from '@helpers/modal-helper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CreateAndUpdateUserModalComponent } from '../../components/create-and-update-user-modal/create-and-update-user-modal.component';
import { AddUserToTenantModalComponent } from '../../components/add-user-to-tenant-modal/add-user-to-tenant-modal.component';

@UntilDestroy()
@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrl: './user-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserListComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly tablePreferencesService = inject(TablePreferencesService);

    private static readonly tableId = 'user-list' as const;

    public readonly align = Align;
    public sortKey: keyof UserResponse = 'lastName';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public loadingUserIds: string[] = [];
    public permissions = Permission;
    public userPermissions: Permission[] = this.permissionService.userPermissions;
    private readonly updateSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public update$: Observable<void> = this.updateSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly sortSubject: BehaviorSubject<SortType<UserResponse>> = new BehaviorSubject({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<UserResponse>> = this.sortSubject.asObservable();
    public users$: Observable<GetUsersResponse>;
    public readonly isUnrestricted = this.permissionService.isUnrestricted();
    public readonly isPrivateTenant = this.permissionService.isPrivateTenant();

    public get f() {
        return this.filterForm.controls;
    }

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(UserListComponent.tableId)?.sort;
        if (saved) {
            this.sortKey = saved.key as keyof UserResponse;
            this.sortAscending = saved.ascending;
            this.sortSubject.next({ key: this.sortKey, ascending: this.sortAscending });
        }

        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.users$ = this.update$.pipe(
            switchMap(() =>
                this.apiClientService.getUsers$().pipe(
                    switchMap((users: GetUsersResponse) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(users);
                            })
                        )
                    ),
                    switchMap((users: GetUsersResponse) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                return of(sortStrings<UserResponse>(users, sort.key, sort.ascending));
                            })
                        )
                    )
                )
            )
        );

        this.f.searchPhrase.valueChanges.subscribe(() => this.searchSubject.next());
    }

    public isDeletable(user: UserResponse): boolean {
        if (this.userPermissions.includes(Permission.WriteUser)) {
            return user.isDeletable;
        }
        return false;
    }
    public onSortChange(sortKey: keyof UserResponse, sortAscending = true) {
        this.tablePreferencesService.setPreferences(UserListComponent.tableId, {
            sort: { key: sortKey as string, ascending: sortAscending }
        });
        this.sortSubject.next({ key: sortKey, ascending: sortAscending });
    }

    private filterResponse(response: GetUsersResponse) {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase();

        return response.filter((user) =>
            Object.values(user).some((value) => typeof value === 'string' && value.toLowerCase().includes(searchPhrase))
        );
    }

    public openUserPanel(editUser?: UserResponse) {
        if (
            this.userPermissions.includes(this.permissions.WriteUser) ||
            editUser?.id === this.authenticationService.getUser().userId
        ) {
            const title = this.translateService.instant('MENU.USER') as string;

            this.rightPanelService.showComponent(
                title,
                CreateAndUpdateUserModalComponent,
                { editUser },
                true,
                false,
                false,
                'person'
            );

            this.rightPanelService.isVisible$
                .pipe(
                    filter((visible: boolean) => !visible), // When panel becomes hidden
                    take(1),
                    untilDestroyed(this)
                )
                .subscribe(() => {
                    this.cachedValuesService.invalidateCache();
                    this.updateSubject.next();
                });
        }
    }

    public openCreateUserModal(editUser?: UserResponse) {
        this.openUserPanel(editUser);
    }

    public openAddUserToTenantPanel() {
        const title = this.translateService.instant('FEATURES.USER.TITLES.ADD_EXISTING_USER') as string;

        this.rightPanelService.showComponent(
            title,
            AddUserToTenantModalComponent,
            {},
            true,
            false,
            false,
            'person_add'
        );

        this.rightPanelService.isVisible$
            .pipe(
                filter((visible: boolean) => !visible),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.cachedValuesService.invalidateCache();
                this.updateSubject.next();
            });
    }

    public confirmDeleteUser(event: Event, user: UserResponse, deleteUser: boolean) {
        event.stopPropagation();
        if (this.loadingUserIds.includes(user.id)) {
            return;
        }
        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);
        const bodyLanguageKey = deleteUser
            ? 'FEATURES.USER.TEXTS.CONFIRM_DELETE_USER'
            : 'FEATURES.USER.TEXTS.CONFIRM_DELETE_USER_FROM_TENANT';
        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.USER.TITLES.DELETE_USER',
            bodyLanguageKey: bodyLanguageKey,
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.loadingUserIds.push(user.id);
                        this.changeDetectorRef.markForCheck();
                        if (!deleteUser) {
                            // If user is not deletable, we remove the user from the tenant instead of deleting the user
                            return this.apiClientService.removeUserFromTenant$(
                                this.authenticationService.getUser().representing.id,
                                user.id
                            );
                        }
                        return this.apiClientService.deleteUser$({ userId: user.id });
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingUserIds = this.loadingUserIds.filter((id) => id != user.id);
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.USER.TOAST_MESSAGES.DELETE_USER_SUCCESS'
                    ) as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.cachedValuesService.invalidateCache();
                    this.updateSubject.next();
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.USER.TOAST_MESSAGES.DELETE_USER_ERROR'
                    ) as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }
}
